import { Fragment } from 'react/jsx-runtime'

import { Kbd } from '@/components/ui/kbd'

import { formatForDisplay } from '@tanstack/react-hotkeys'
import { cn } from 'tailwind-variants'

import type { RegisterableHotkey } from '@tanstack/react-hotkeys'

export type HotkeyValue = RegisterableHotkey | readonly RegisterableHotkey[]

/**
 * `formatForDisplay` renders Space as the glyph U+2423 (␣), which reads as a
 * smudge at this size. Spell the key out instead.
 */
function formatHotkeyLabel(hotkey: RegisterableHotkey): string {
  return formatForDisplay(hotkey, { separatorToken: '+' }).replace(
    /\u2423/g,
    'Space',
  )
}

export interface KbdShortcutsProps extends Omit<
  React.ComponentProps<typeof Kbd>,
  'children'
> {
  hotkey: HotkeyValue
  separator?: React.ReactNode
  separatorClassName?: string
}

export function KbdShortcuts({
  hotkey,
  separator = 'or',
  separatorClassName,
  ...kbdProps
}: KbdShortcutsProps) {
  const hotkeys: readonly RegisterableHotkey[] = Array.isArray(hotkey)
    ? hotkey
    : [hotkey as RegisterableHotkey]

  return (
    <>
      {hotkeys.map((value, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <span
              className={cn('mx-1 text-muted-foreground', separatorClassName)}
            >
              {separator}
            </span>
          )}

          <Kbd {...kbdProps}>{formatHotkeyLabel(value)}</Kbd>
        </Fragment>
      ))}
    </>
  )
}
